import * as React from 'react';
import { useState, useMemo, useRef } from 'react';

/** Material UI Packages. */
import { Typography, Box, TextField, Autocomplete, Popper, Chip } from '@mui/material';
import { styled } from '@mui/material/styles';
import useEnhancedEffect from '@mui/material/utils/useEnhancedEffect';

/** Material UI Icons. */
import CancelIcon from '@mui/icons-material/Cancel';
import { isAssistantManagerOrAbove } from 'shared/lib/roles';

const StyledPopper = styled(Popper)({
    '& .MuiAutocomplete-listbox': {
        fontSize: '13px',
        overflowY: 'auto',
    },
    overflowY: 'auto',
});

/** Value holds the ID, but the label displays the CONTENT value. */
export function AutocompleteField({
    label,
    sx,
    value,
    onChange,
    onInputChange,
    name,
    options,
    AutocompleteStyle,
    labelStyle,
    disable,
    additional,
}) {
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '25px',
                ...sx,
            }}
        >
            <Typography
                sx={{
                    display: 'inline',
                    color: '#003a52',
                    fontSize: '13px',
                    fontWeight: 500,
                    width: '100px',
                    ...labelStyle,
                }}
            >
                {label}
            </Typography>
            <Autocomplete
                size="small"
                value={options?.find((option) => option.id === value) || null}
                getOptionLabel={(option) => option.content}
                isOptionEqualToValue={(option, value) => option.id === value}
                onChange={onChange}
                onInputChange={onInputChange}
                options={options}
                name={name}
                slotProps={{
                    popper: {
                        component: StyledPopper,
                    },
                }}
                sx={{
                    height: '27px',
                    '& .MuiInputBase-input': {
                        fontSize: '13px',
                        height: '12px',
                    },
                    ...AutocompleteStyle,
                }}
                renderInput={(params) => <TextField {...params} label="" name={name} />}
                renderOption={(props, option) => (
                    <li
                        {...props}
                        key={option.id}
                        style={{ margin: 0, padding: 0, paddingLeft: '5px', paddingBottom: '5px' }}
                    >
                        {option.content}
                    </li>
                )}
                disabled={!disable}
                {...additional}
            />
        </Box>
    );
}

/** Value & label apply the CONTENT value. */
export function AutocompleteField_Single({
    label,
    sx,
    value,
    onChange,
    name,
    options,
    width,
    labelStyle,
    getOptionLabel,
    isOptionEqualToValue,
    disablePortal,
    isError,
    disableClearable = false,
}) {
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '25px',
                ...sx,
            }}
        >
            <Typography
                sx={{
                    display: 'inline',
                    color: '#003a52',
                    fontSize: '13px',
                    fontWeight: 500,
                    width: '100px',
                    ...labelStyle,
                }}
            >
                {label}
            </Typography>
            <Autocomplete
                size="small"
                value={value}
                onChange={(event, newValue) => onChange(event, newValue, name)}
                disablePortal={disablePortal}
                options={options}
                name={name}
                aria-label={name}
                disableClearable={disableClearable}
                slotProps={{
                    popper: {
                        component: StyledPopper,
                    },
                }}
                getOptionLabel={getOptionLabel}
                isOptionEqualToValue={isOptionEqualToValue}
                sx={{
                    width: width,
                    height: '27px',
                    '& .MuiInputBase-input': {
                        fontSize: '13px',
                        height: '12px',
                    },
                }}
                renderInput={(params) => (
                    <TextField {...params} error={isError} label="" name={String(name)} aria-label={name} />
                )}
            />
        </Box>
    );
}

/** Value holds the ID, but the label displays the CONTENT value. Mulitiple choices are available. */
export function MultiAutocompleteField({
    label,
    sx,
    value,
    onChange,
    name,
    options,
    autocompleteStyle,
    labelStyle,
    isError,
    additional,
}) {
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '25px',
                width: '100%',
                height: 'auto',
                ...sx,
            }}
        >
            <Typography
                sx={{
                    display: 'inline',
                    color: '#003a52',
                    fontSize: '13px',
                    fontWeight: 500,
                    width: '100px',
                    ...labelStyle,
                }}
            >
                {label}
            </Typography>
            <Autocomplete
                size="small"
                multiple
                value={value}
                getOptionLabel={(option) => option.name}
                onChange={(event, newValue) => onChange({ target: { dataset: { name } } }, newValue)}
                disablePortal
                options={options || []}
                name={name}
                slotProps={{
                    popper: {
                        component: StyledPopper,
                    },
                }}
                sx={{
                    width: '200px',
                    height: '27px',
                    '& .MuiInputBase-input': {
                        fontSize: '13px',
                        height: '12px',
                    },
                    ...autocompleteStyle,
                }}
                renderInput={(params) => <TextField {...params} label="" data-name={name} error={isError} />}
                renderOption={(props, option) => {
                    return (
                        <li {...props} key={option.id} data-name={name}>
                            {option?.name ?? ''}
                        </li>
                    );
                }}
                renderTags={(value, getTagProps) =>
                    value.map((option, index) => {
                        const { key, ...tagProps } = getTagProps({ index });
                        return (
                            <Chip
                                variant="outlined"
                                label={option?.name ?? ''}
                                key={key}
                                {...tagProps}
                                size="small"
                                deleteIcon={<CancelIcon name={name} size="small" />}
                                sx={{
                                    fontSize: '11px',
                                }}
                            />
                        );
                    })
                }
                {...additional}
            />
        </Box>
    );
}
/** Value holds the Object(id, name), but the label displays the NAME value. */
export function AutocompleteFieldTradePartner({
    label,
    sx,
    value,
    onChange,
    onInputChange,
    onFocus,
    name,
    options,
    AutocompleteStyle,
    labelStyle,
    disable,
    disableClearable,
    error,
}) {
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '25px',
                ...sx,
            }}
        >
            <Typography
                sx={{
                    display: 'inline',
                    color: '#003a52',
                    fontSize: '13px',
                    fontWeight: 500,
                    width: '100px',
                    ...labelStyle,
                }}
            >
                {label}
            </Typography>
            <Autocomplete
                size="small"
                value={value || null}
                getOptionLabel={(option) => option.name || ''}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onChange={onChange}
                onInputChange={onInputChange}
                options={options}
                name={name}
                slotProps={{
                    popper: {
                        component: StyledPopper,
                    },
                }}
                sx={{
                    '& .MuiInputBase-input': {
                        fontSize: '13px',
                        height: '12px',
                    },
                    ...AutocompleteStyle,
                }}
                renderInput={(params) => <TextField {...params} label="" name={name} error={error} onFocus={onFocus} />}
                renderOption={(props, option) => (
                    <li
                        {...props}
                        key={option.id}
                        style={{ margin: 0, padding: 0, paddingLeft: '5px', paddingBottom: '5px' }}
                    >
                        {option.name}
                    </li>
                )}
                //굳이 필요한가? 확인해야함.
                // filterOptions={(options) => {
                //     return options;
                // }}
                disabled={!disable}
                disableClearable={disableClearable}
            />
        </Box>
    );
}

function isSameIataFirmsOption(option, value) {
    if (option?.id != null && value?.id != null) return option.id === value.id;
    return getIataFirmsOptionLabel(option) === getIataFirmsOptionLabel(value);
}

function getIataFirmsOptionLabel(option) {
    if (option == null) return '';
    if (typeof option === 'string') return option;
    const iata = option.iata_firms != null && option.iata_firms !== '' ? String(option.iata_firms) : '';
    const name = option.name != null && option.name !== '' ? String(option.name) : '';
    if (iata && name) return `${iata}|${name}`;
    if (iata) return iata;
    if (name) return name;
    return '';
}

/** Value holds the Object(id, name), but the label displays the NAME value. */
export function AutocompleteField_String({
    label,
    sx,
    value,
    onChange,
    onInputChange,
    onFocus,
    name,
    options,
    AutocompleteStyle,
    labelStyle,
    disable,
    freeSolo,
    useCustomComparisons = false,
    userRole,
    disableClearable = false,
}) {
    const getOptionDisabled = useMemo(() => {
        return useCustomComparisons
            ? (option) =>
                  option.id === 220 && !isAssistantManagerOrAbove(userRole)
            : null;
    }, [useCustomComparisons, userRole]);

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '25px',
                ...sx,
            }}
        >
            <Typography
                sx={{
                    display: 'inline',
                    color: '#003a52',
                    fontSize: '13px',
                    fontWeight: 500,
                    width: '100px',
                    ...labelStyle,
                }}
            >
                {label}
            </Typography>
            <Autocomplete
                freeSolo={freeSolo}
                size="small"
                value={value || ''}
                // getOptionLabel={(option) => (typeof option === 'string' ? option : option.name)}
                getOptionLabel={(option) => getIataFirmsOptionLabel(option)}
                isOptionEqualToValue={isSameIataFirmsOption}
                onChange={onChange}
                onInputChange={onInputChange}
                options={options || []}
                name={name}
                getOptionDisabled={getOptionDisabled}
                slotProps={{
                    popper: {
                        component: StyledPopper,
                    },
                }}
                sx={{
                    height: '27px',
                    '& .MuiInputBase-input': {
                        fontSize: '13px',
                        height: '12px',
                    },

                    ...AutocompleteStyle,
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label=""
                        name={name}
                        onFocus={onFocus}
                        InputProps={{
                            ...params.InputProps,
                            style: {
                                paddingLeft: 1, // 아이콘 버튼과 텍스트 필드 사이 패딩 줄임
                                paddingRight: 30, // 아이콘 버튼과 텍스트 필드 사이 패딩 줄임
                            },
                        }}
                        sx={{
                            '& .MuiInputBase-input': {
                                px: 0,
                                fontSize: 12,
                            },
                        }}
                    />
                )}
                renderOption={(props, option) => (
                    <li
                        {...props}
                        key={typeof option === 'string' ? option : option.id}
                        style={{ margin: 0, padding: 0, paddingLeft: '5px', paddingBottom: '5px' }}
                    >
                        {getIataFirmsOptionLabel(option)}
                    </li>
                )}
                disabled={disable}
                disableClearable={disableClearable}
            />
        </Box>
    );
}
/** Value holds the Object(id, name, code), but the option displays the CODE | NAME value and the label displays the NAME only.  */
export function AutocompleteField_CodeName({
    label,
    sx,
    value,
    onChange,
    onInputChange,
    onFocus,
    name,
    options,
    AutocompleteStyle,
    labelStyle,
    disable,
    displayKey,
}) {
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '25px',
                ...sx,
            }}
        >
            <Typography
                sx={{
                    display: 'inline',
                    color: '#003a52',
                    fontSize: '13px',
                    fontWeight: 500,
                    width: '100px',
                    ...labelStyle,
                }}
            >
                {label}
            </Typography>
            <Autocomplete
                size="small"
                value={value || null}
                getOptionLabel={(option) => {
                    return displayKey === 'name' ? option.name : option.code;
                }}
                isOptionEqualToValue={(option, value) => {
                    return option.id === value?.id;
                }}
                onChange={onChange}
                onInputChange={onInputChange}
                options={options}
                name={name}
                slotProps={{
                    popper: {
                        component: StyledPopper,
                    },
                }}
                sx={{
                    '& .MuiInputBase-input': {
                        fontSize: '13px',
                        height: '12px',
                    },
                    ...AutocompleteStyle,
                }}
                renderInput={(params) => <TextField {...params} label="" name={name} onFocus={onFocus} />}
                renderOption={(props, option) => {
                    return (
                        <li
                            {...props}
                            key={option.id}
                            style={{ margin: 0, padding: 0, paddingLeft: '5px', paddingBottom: '5px' }}
                        >
                            {option.code} | {option.name}
                        </li>
                    );
                }}
                filterOptions={(options) => {
                    return options;
                }}
                disabled={!disable}
            />
        </Box>
    );
}

/** Styled Popper component for customizing the appearance of the Autocomplete dropdown. */
const VolumeStyledPopper = styled(Popper)({
    '& .MuiAutocomplete-listbox': {
        fontSize: '12px',
        height: '100px',
    },
});

export const VolumeAutocomplete = (props) => {
    const { id, value, field, hasFocus } = props;
    const { rows, options } = props;
    const [editValue, setEditValue] = useState(rows.filter((item) => item.id === id)[0].volume || '');

    const ref = useRef(null);

    const handleChange = (event, newValue) => {
        props.api.setEditCellValue({
            id,
            field,
            value: newValue,
        });
    };

    useEnhancedEffect(() => {
        if (hasFocus && ref.current) {
            const input = ref.current.querySelector(`input`);
            input?.focus();
        }
    }, [hasFocus, value]);

    return (
        <Autocomplete
            ref={ref}
            value={editValue}
            options={options}
            disableClearable
            onChange={handleChange}
            onInputChange={(event, newValue, reason) => {
                setEditValue(newValue);
            }}
            renderInput={(params) => <TextField {...params} size="small" />}
            slotProps={{
                popper: {
                    component: VolumeStyledPopper,
                },
            }}
            sx={{
                width: '100%',
                '& .MuiInputBase-input': {
                    fontSize: '12px',
                },
            }}
        />
    );
};
